import { useMemo } from 'react';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

export interface CognitoConfig {
    userPoolId: string;
    clientId: string;
}

export interface CognitoUser {
    user: AmazonCognitoIdentity.CognitoUser;
    userPool: AmazonCognitoIdentity.CognitoUserPool;
}

export interface UseCognitoReturn {
    userPool: AmazonCognitoIdentity.CognitoUserPool;
    createUser: (username: string) => CognitoUser;
    authenticateUser: (
        username: string,
        password: string,
        callbacks: {
            onSuccess: (result: AmazonCognitoIdentity.CognitoUserSession) => void;
            onFailure: (err: any) => void;
            newPasswordRequired?: (userAttributes: any, requiredAttributes: any) => void;
            mfaRequired?: (challengeName: string, challengeParameters: any) => void;
        }
    ) => AmazonCognitoIdentity.CognitoUser;
    completeNewPasswordChallenge: (
        cognitoUser: AmazonCognitoIdentity.CognitoUser,
        newPassword: string,
        userAttributes: Record<string, string>,
        callbacks: {
            onSuccess: (result: AmazonCognitoIdentity.CognitoUserSession) => void;
            onFailure: (err: any) => void;
        },
        requiredAttributes?: string[]
    ) => void;
    forgotPassword: (
        username: string,
        callbacks: {
            onSuccess: () => void;
            onFailure: (err: any) => void;
        }
    ) => AmazonCognitoIdentity.CognitoUser;
    confirmPassword: (
        cognitoUser: AmazonCognitoIdentity.CognitoUser,
        verificationCode: string,
        newPassword: string,
        callbacks: {
            onSuccess: () => void;
            onFailure: (err: any) => void;
        }
    ) => void;
    getTokenId: (callbacks: { onSuccess: (idToken: string) => void; onFailure: (err: any) => void }) => void;
    getAccessToken: (callbacks: { onSuccess: (accessToken: string) => void; onFailure: (err: any) => void }) => void;
    getCurrentUser: () => AmazonCognitoIdentity.CognitoUser | null;
    config: CognitoConfig;
}

/**
 * Custom hook for AWS Cognito User Pool operations
 * Centralizes Cognito configuration and provides common authentication methods
 */
export const useCognito = (): UseCognitoReturn => {
    const config = useMemo<CognitoConfig>(() => {
        const userPoolId = process.env.REACT_APP_USER_POOL_ID;
        const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;

        if (!userPoolId || !clientId) {
            throw new Error('AWS Cognito configuration is missing. Please check your environment variables.');
        }

        return {
            userPoolId,
            clientId,
        };
    }, []);

    const userPool = useMemo(() => {
        return new AmazonCognitoIdentity.CognitoUserPool({
            UserPoolId: config.userPoolId,
            ClientId: config.clientId,
        });
    }, [config]);

    const createUser = (username: string): CognitoUser => {
        const userData = {
            Username: username,
            Pool: userPool,
        };

        const user = new AmazonCognitoIdentity.CognitoUser(userData);

        return {
            user,
            userPool,
        };
    };

    const authenticateUser = (
        username: string,
        password: string,
        callbacks: {
            onSuccess: (result: AmazonCognitoIdentity.CognitoUserSession) => void;
            onFailure: (err: any) => void;
            newPasswordRequired?: (userAttributes: any, requiredAttributes: any) => void;
            mfaRequired?: (challengeName: string, challengeParameters: any) => void;
        }
    ): AmazonCognitoIdentity.CognitoUser => {
        const { user } = createUser(username);

        const authenticationData = {
            Username: username,
            Password: password,
        };

        const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);

        user.authenticateUser(authenticationDetails, callbacks);
        return user;
    };

    const completeNewPasswordChallenge = (
        cognitoUser: AmazonCognitoIdentity.CognitoUser,
        newPassword: string,
        userAttributes: Record<string, string>,
        callbacks: {
            onSuccess: (result: AmazonCognitoIdentity.CognitoUserSession) => void;
            onFailure: (err: any) => void;
        },
        requiredAttributes: string[] = []
    ) => {
        // Cognito returns existing attrs (incl. custom:*) on newPasswordRequired;
        // echoing them fails when the app client cannot write them. Only send
        // attributes Cognito listed as required for this challenge (usually none).
        const attrs: Record<string, string> = {};
        for (const name of requiredAttributes) {
            if (name === 'email' || name === 'email_verified' || name === 'sub' || name.startsWith('custom:')) {
                continue;
            }
            const value = userAttributes[name];
            if (typeof value === 'string' && value.length > 0) {
                attrs[name] = value;
            }
        }
        cognitoUser.completeNewPasswordChallenge(newPassword, attrs, callbacks);
    };

    const forgotPassword = (
        username: string,
        callbacks: {
            onSuccess: () => void;
            onFailure: (err: any) => void;
        }
    ): AmazonCognitoIdentity.CognitoUser => {
        const { user } = createUser(username);

        user.forgotPassword(callbacks);

        return user;
    };

    const confirmPassword = (
        cognitoUser: AmazonCognitoIdentity.CognitoUser,
        verificationCode: string,
        newPassword: string,
        callbacks: {
            onSuccess: () => void;
            onFailure: (err: any) => void;
        }
    ) => {
        cognitoUser.confirmPassword(verificationCode, newPassword, callbacks);
    };

    const getTokenId = (callbacks: { onSuccess: (idToken: string) => void; onFailure: (err: any) => void }) => {
        const currentUser = userPool.getCurrentUser();

        if (!currentUser) {
            callbacks.onFailure(new Error('No authenticated user found'));
            return;
        }

        currentUser.getSession((err: any, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
            if (err) {
                callbacks.onFailure(err);
                return;
            }

            if (!session || !session.isValid()) {
                callbacks.onFailure(new Error('Invalid or expired session'));
                return;
            }

            const idToken = session.getIdToken().getJwtToken();
            callbacks.onSuccess(idToken);
        });
    };

    const getAccessToken = (callbacks: { onSuccess: (accessToken: string) => void; onFailure: (err: any) => void }) => {
        const currentUser = userPool.getCurrentUser();

        if (!currentUser) {
            callbacks.onFailure(new Error('No authenticated user found'));
            return;
        }

        currentUser.getSession((err: any, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
            if (err) {
                callbacks.onFailure(err);
                return;
            }

            if (!session || !session.isValid()) {
                callbacks.onFailure(new Error('Invalid or expired session'));
                return;
            }

            const accessToken = session.getAccessToken().getJwtToken();
            callbacks.onSuccess(accessToken);
        });
    };

    const getCurrentUser = (): AmazonCognitoIdentity.CognitoUser | null => {
        return userPool.getCurrentUser();
    };

    return {
        userPool,
        createUser,
        authenticateUser,
        completeNewPasswordChallenge,
        forgotPassword,
        confirmPassword,
        getTokenId,
        getAccessToken,
        getCurrentUser,
        config,
    };
};
